// "Add your representation" (Figma 22).
//
// Three outcomes, and the design is deliberate about which is easiest: an actor
// with an agent should submit through them, so the agency search leads. Sending
// self-represented is always available and never hidden behind a confirmation —
// most people arriving here genuinely are.
//
// The change is made in ProfileManagement, as on the actor's profile (docs/30
// §9): an agent from the agency's list, or one the actor names, whom PM adds
// as pending and emails for approval. PM needs a named agent's email and phone.
// Only the submission on screen goes through that agent; nothing else changes.

import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import i18next from "i18next";
import SubmissionSheet from "./SubmissionSheet";
import {
  getSubmissionFlowApi,
  IAgencyOptionDto,
  IAgentOptionDto,
  IRepresentationDto,
  ISetRepresentationRequest,
} from "../../../shared/api/submission";
import { emailRejectionMessage, isValidEmail } from "../../../helpers/validations";
import { membershipPrimary } from "./membershipButton";

// Long enough that the first keystroke does not fire a request, short enough
// that the list feels immediate.
const SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY = 2;

interface Props {
  current?: IRepresentationDto;
  onCancel: () => void;
  // Resolves to null when saved, or the error code when it wasn't.
  onSave: (request: ISetRepresentationRequest) => Promise<string | null>;
}

const saveErrorMessage = (code: string): string =>
  code === "agentNotAdded"
    ? i18next.t("We couldn't add that agent. Pick them from the agency's list, or check their email.")
    : code === "invalidRepresentation"
    ? i18next.t("Check your agent's details and try again.")
    : i18next.t("We couldn't save that. Please try again.");

const RepresentationSheet: React.FC<Props> = ({ current, onCancel, onSave }) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<IAgencyOptionDto[]>([]);
  const [chosen, setChosen] = useState<IAgencyOptionDto | null>(null);
  // The chosen agency's agents, and the one picked from them.
  const [agents, setAgents] = useState<IAgentOptionDto[]>([]);
  const [agentId, setAgentId] = useState<number | null>(null);
  // Naming an agent who isn't on the agency's list (or inviting one).
  const [notListed, setNotListed] = useState(false);
  const [agentName, setAgentName] = useState("");
  const [agentEmail, setAgentEmail] = useState("");
  const [agentPhone, setAgentPhone] = useState("");
  const [inviting, setInviting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<any>(null);
  // Guards against an earlier, slower search landing after a later one and
  // repopulating the list with results for a query nobody is looking at.
  const queryRef = useRef("");

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const runSearch = (value: string) => {
    queryRef.current = value;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (value.trim().length < MIN_QUERY) {
      setOptions([]);
      return;
    }
    timerRef.current = setTimeout(() => {
      getSubmissionFlowApi()
        .searchAgencies(value)
        .then((results) => {
          if (queryRef.current === value) setOptions(results);
        })
        .catch(() => setOptions([]));
    }, SEARCH_DEBOUNCE_MS);
  };

  const onQueryChange = (value: string) => {
    setQuery(value);
    setChosen(null);
    setAgents([]);
    setAgentId(null);
    setNotListed(false);
    setError(null);
    runSearch(value);
  };

  const onChooseAgency = (option: IAgencyOptionDto) => {
    setChosen(option);
    setQuery(option.agencyName);
    setOptions([]);
    setError(null);
    setAgentId(null);
    getSubmissionFlowApi()
      .listAgencyAgents(option.agencyId)
      .then((list) => {
        setAgents(list);
        // Nobody listed: the actor names their agent.
        setNotListed(list.length === 0);
      })
      .catch(() => {
        setAgents([]);
        setNotListed(true);
      });
  };

  const submit = async (request: ISetRepresentationRequest) => {
    setSaving(true);
    setError(null);
    const failed = await onSave(request);
    setSaving(false);
    if (failed) setError(saveErrorMessage(failed));
  };

  // A named agent: their name, and the email and phone PM asks for. The email is
  // screened against the shared domain lists, not just the shape: PM emails the
  // agent the approval link, and a disposable or misspelled domain means they
  // never see it.
  const agentDetailsError = (): string | null => {
    if (!agentName.trim() || !agentEmail.trim() || !agentPhone.trim()) {
      return i18next.t("We need your agent's name, email and phone.");
    }
    if (!isValidEmail(agentEmail.trim())) {
      return emailRejectionMessage(
        agentEmail.trim(),
        i18next.t.bind(i18next),
        i18next.t("That email address doesn't look right.")
      );
    }
    return null;
  };

  const agentDetails = () => ({
    agentName: agentName.trim(),
    agentEmail: agentEmail.trim(),
    agentPhone: agentPhone.trim(),
  });

  const onSaveAgency = () => {
    if (!chosen) {
      setError(i18next.t("Pick your agency from the list, or invite them below."));
      return;
    }
    if (agentId) {
      submit({ type: "agency", agencyId: chosen.agencyId, agencyName: chosen.agencyName, agentId });
      return;
    }
    if (!notListed) {
      setError(i18next.t("Pick your agent from the list, or add their details."));
      return;
    }
    const invalid = agentDetailsError();
    if (invalid) {
      setError(invalid);
      return;
    }
    submit({ type: "agency", agencyId: chosen.agencyId, agencyName: chosen.agencyName, ...agentDetails() });
  };

  const onSaveInvite = () => {
    if (!query.trim()) {
      setError(i18next.t("We need the agency name, and your agent's name, email and phone."));
      return;
    }
    const invalid = agentDetailsError();
    if (invalid) {
      setError(invalid);
      return;
    }
    submit({ type: "invited", agencyName: query.trim(), ...agentDetails() });
  };

  const showDetails = inviting || (!!chosen && notListed);

  return (
    <SubmissionSheet
      title={i18next.t("Add your representation")}
      onDismiss={saving ? undefined : onCancel}
      /* An agency search and up to four fields: a mis-click on the scrim would
         clear all of it. Escape still closes. */
      dismissOnScrim={false}
    >
      <SheetText>
        {i18next.t("If you have representation, it's better to proceed with them here.")}
      </SheetText>

      <Field
        value={query}
        onChange={(e: any) => onQueryChange(e.target.value)}
        placeholder={i18next.t("Search agency")}
        aria-label={i18next.t("Search agency")}
        autoComplete="off"
      />

      {!inviting && options.length > 0 && (
        <Options role="listbox" aria-label={i18next.t("Search agency")}>
          {options.map((option) => (
            <Option
              key={option.agencyId}
              type="button"
              role="option"
              aria-selected={!!chosen && chosen.agencyId === option.agencyId}
              selected={!!chosen && chosen.agencyId === option.agencyId}
              onClick={() => onChooseAgency(option)}
            >
              <OptionName>{option.agencyName}</OptionName>
              {option.cityName && <OptionCity>{option.cityName}</OptionCity>}
            </Option>
          ))}
        </Options>
      )}

      {!inviting && chosen && agents.length > 0 && !notListed && (
        <>
          <FieldLabel>{i18next.t("Pick your agent")}</FieldLabel>
          <Options role="listbox" aria-label={i18next.t("Pick your agent")}>
            {agents.map((agent) => (
              <Option
                key={agent.agentId}
                type="button"
                role="option"
                aria-selected={agentId === agent.agentId}
                selected={agentId === agent.agentId}
                onClick={() => {
                  setAgentId(agent.agentId);
                  setError(null);
                }}
              >
                <OptionName>{agent.agentName}</OptionName>
              </Option>
            ))}
          </Options>
          {chosen.allowNewAgents !== false && (
            <TextButton
              type="button"
              onClick={() => {
                setNotListed(true);
                setAgentId(null);
                setError(null);
              }}
              disabled={saving}
            >
              {i18next.t("My agent isn't listed")}
            </TextButton>
          )}
        </>
      )}

      {showDetails && (
        <>
          <Field
            value={agentName}
            onChange={(e: any) => setAgentName(e.target.value)}
            placeholder={i18next.t("Agent name")}
            aria-label={i18next.t("Agent name")}
            autoComplete="off"
          />
          <Field
            value={agentEmail}
            onChange={(e: any) => setAgentEmail(e.target.value)}
            placeholder={i18next.t("Agent email")}
            aria-label={i18next.t("Agent email")}
            type="email"
            autoComplete="off"
          />
          <Field
            value={agentPhone}
            onChange={(e: any) => setAgentPhone(e.target.value)}
            placeholder={i18next.t("Agent phone")}
            aria-label={i18next.t("Agent phone")}
            type="tel"
            autoComplete="off"
          />
        </>
      )}

      {error && <SheetError role="alert">{error}</SheetError>}

      {inviting ? (
        <PrimaryButton onClick={onSaveInvite} disabled={saving}>
          {saving ? i18next.t("One moment…") : i18next.t("Send the invite")}
        </PrimaryButton>
      ) : (
        <PrimaryButton onClick={onSaveAgency} disabled={saving}>
          {saving ? i18next.t("One moment…") : i18next.t("Continue")}
        </PrimaryButton>
      )}

      <TextButton
        type="button"
        onClick={() => {
          setInviting(!inviting);
          setChosen(null);
          setAgents([]);
          setAgentId(null);
          setNotListed(false);
          setError(null);
        }}
        disabled={saving}
      >
        {inviting
          ? i18next.t("Search for an agency instead")
          : i18next.t("Agent not in the system? Invite them")}
      </TextButton>

      {/* Back to the default, for this submission. The agent stays on the
          actor's profile. */}
      <TextButton
        type="button"
        onClick={() => submit({ type: "selfRepresented" })}
        disabled={saving}
      >
        {i18next.t("Send as self-represented")}
      </TextButton>
    </SubmissionSheet>
  );
};

const FieldLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #122640;
  text-align: left;
`;

const SheetText = styled.p`
  font-size: 14px;
  color: #53627c;
  line-height: 1.5;
  margin: 0 0 4px;
`;

const Field = styled.input`
  width: 100%;
  height: 48px;
  min-height: 48px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #c9d3e0;
  border-radius: 5px;
  font-size: 15px;
  color: #122640;
  box-sizing: border-box;
  outline: 0;

  &:focus {
    border-color: #0f9bf0;
  }
`;

const Options = styled.div`
  border: 1px solid #e3e8ee;
  border-radius: 6px;
  overflow: hidden;
  text-align: left;
`;

const Option: any = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: ${(p: any) => (p.selected ? "#eaf6fe" : "none")};
  border: none;
  border-bottom: 1px solid #eef1f5;
  padding: 10px 12px;
  cursor: pointer;
  min-height: 44px;

  &:last-child {
    border-bottom: none;
  }
`;

const OptionName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #122640;
`;

const OptionCity = styled.div`
  font-size: 12px;
  color: #53627c;
`;

const SheetError = styled.div`
  font-size: 13px;
  color: #c7053f;
  margin: 0;
`;

const PrimaryButton = styled.button`
  width: 100%;
  flex: none;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

const TextButton = styled.button`
  display: block;
  width: 100%;
  flex: none;
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 0;
  cursor: pointer;
  min-height: 40px;

  &:disabled {
    color: #8a94a6;
    cursor: default;
  }
`;

export default RepresentationSheet;
